import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';

/**
 * Customer-safe activity feed.
 *
 * audit_log itself is readable by superadmin only, so the portal reads an RPC:
 * a narrow projection (ticket, field-visit, site, document and system events)
 * with a summary line and an actor KIND (system / paznetworks / customer) —
 * never a staff name.
 *
 * One call, however many accounts. This used to fan out one request per linked
 * account and merge in the browser, which on the seeded Stanford portfolio meant
 * SIXTEEN round trips per dashboard load, fetching 160 rows to show 10. The
 * round trips were the least of it: merging client-side also made pagination
 * silently wrong (an offset means nothing until the accounts are merged, so the
 * hook asked every account for its first page and page 2 was unreachable) and
 * made a total impossible (the browser sees N partial lists and cannot know how
 * many events exist). Both are properties of the whole ordered set, which only
 * the database holds — see
 * `20260921130003_activity_feed_across_linked_accounts.sql`.
 */
export interface ActivityEvent {
  id: string;
  occurred_at: string;
  action: string;
  entity_type: string;
  entity_id: string;
  /** Which linked account the event belongs to. */
  customer_id: string | null;
  location_id: string | null;
  summary: string;
  actor_kind: 'system' | 'paznetworks' | 'customer' | string;
}

export interface ActivityFeedResult {
  events: ActivityEvent[];
  /** Total matching rows across every account asked for. */
  totalCount: number;
}

interface ActivityFeedOptions {
  limit?: number;
  page?: number;
  locationId?: string;
  /** Restrict to one account (e.g. the owner of a location). */
  customerId?: string | null;
  /** Hold the query until the caller knows which account to ask for. */
  enabled?: boolean;
}

async function fetchFeed(
  customerIds: string[],
  limit: number,
  offset: number,
  locationId?: string,
): Promise<ActivityFeedResult> {
  const { data, error } = await supabase.rpc('customer_activity_feed_scoped', {
    p_customer_ids: customerIds,
    p_limit: limit,
    p_offset: offset,
    ...(locationId ? { p_location_id: locationId } : {}),
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<ActivityEvent & { total_count: number | null }>;
  return {
    events: rows.map(({ total_count: _total, ...event }) => event),
    // The window function counts the whole filtered set, so this is the
    // portfolio total rather than the page length. Zero rows means zero events.
    totalCount: rows[0]?.total_count ?? 0,
  };
}

export function useActivityFeed(options?: ActivityFeedOptions) {
  const scope = useCustomerScope();
  const limit = Math.min(Math.max(options?.limit ?? 10, 1), 100);
  const page = Math.max(options?.page ?? 0, 0);
  const locationId = options?.locationId;
  // An explicit account outside the caller's linked set yields no ids at all,
  // so the query stays disabled rather than quietly widening to the portfolio.
  // The RPC drops unauthorised ids too; this is the same rule stated twice
  // deliberately, because relying on the server alone would still let the UI
  // ask a question it has no business asking.
  const ids =
    options?.customerId && scope.ids.includes(options.customerId)
      ? [options.customerId]
      : options?.customerId
        ? []
        : scope.ids;

  // Sorted, so the same set of accounts is one cache entry however it arrives.
  const idKey = ids.slice().sort();

  return useQuery({
    queryKey: ['activity-feed', scope.ownerKey, idKey.join(','), limit, page, locationId ?? ''],
    enabled: (options?.enabled ?? true) && scope.ready && ids.length > 0,
    queryFn: (): Promise<ActivityFeedResult> =>
      fetchFeed(idKey, limit, page * limit, locationId),
    staleTime: 60 * 1000,
  });
}
